import { useMemo, useState } from 'react'
import {
  BarChart3,
  MessageSquare,
  Phone,
  PhoneMissed,
  Timer,
  TrendingDown,
  TrendingUp,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { format } from 'date-fns'
import { enCA, fr } from 'date-fns/locale'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

interface ChartTooltipProps {
  active?: boolean
  label?: string
  payload?: Array<{ name?: string; value?: number; color?: string }>
}
import { cn } from '@/lib/utils'
import { useAgentStatistics } from '@/api/analytics'
import { useUIStore } from '@/stores/useUIStore'
import type { AgentStats, AnalyticsPeriod } from '@/types/analytics'

const DAYS_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']

function formatDurationFromMinutes(minutes: number): { value: string; unit: string } {
  const totalSeconds = Math.round(minutes * 60)
  const hours = Math.floor(totalSeconds / 3600)
  const mins = Math.floor((totalSeconds % 3600) / 60)
  if (hours > 0) {
    return { value: `${hours}h ${String(mins).padStart(2, '0')}m`, unit: '' }
  }
  return { value: `${mins}`, unit: 'min' }
}

interface AnalyticsTabProps {
  /** Scope sur un agent précis (managers uniquement). Sans valeur = user connecté. */
  agentId?: number
  /** Masque le header du composant (utile quand on l'embed dans un panel qui a déjà son propre header). */
  hideHeader?: boolean
}

export function AnalyticsTab({ agentId, hideHeader }: AnalyticsTabProps = {}) {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const darkMode = useUIStore((s) => s.darkMode)

  const [period, setPeriod] = useState<AnalyticsPeriod>('cette_semaine')
  const { data: stats, isLoading, error, refetch } = useAgentStatistics(period, agentId)

  const cardBg = darkMode
    ? 'bg-[#1e293b] border-slate-700'
    : 'bg-white border-slate-200'
  const pageBg = darkMode ? 'bg-[#0f172a]' : 'bg-slate-50'

  if (isLoading && !stats) {
    return (
      <div className={cn('flex-1 flex items-center justify-center h-full', pageBg)}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          <p
            className={cn(
              'text-sm',
              darkMode ? 'text-slate-400' : 'text-slate-500',
            )}
          >
            {t('analytics.loading', 'Chargement...')}
          </p>
        </div>
      </div>
    )
  }

  if (error || !stats) {
    return (
      <div className={cn('flex-1 flex items-center justify-center h-full', pageBg)}>
        <div className="text-center">
          <p className="text-rose-500 mb-2">
            {error instanceof Error
              ? error.message
              : t('analytics.noData', 'Pas de données')}
          </p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="text-sm text-indigo-500 hover:underline"
          >
            {t('analytics.retry', 'Réessayer')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('h-full overflow-y-auto', pageBg)}>
      <div className="py-8 px-6 flex flex-col gap-8 max-w-7xl mx-auto">
        {!hideHeader && (
          <Header period={period} onChangePeriod={setPeriod} darkMode={darkMode} />
        )}
        {hideHeader && (
          <div className="flex justify-end">
            <PeriodSelectOnly
              period={period}
              onChange={setPeriod}
              darkMode={darkMode}
            />
          </div>
        )}
        <KPIGrid stats={stats} darkMode={darkMode} cardBg={cardBg} />
        <CallVolumeChart
          stats={stats}
          darkMode={darkMode}
          cardBg={cardBg}
          locale={locale}
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DirectionSplit stats={stats} darkMode={darkMode} cardBg={cardBg} />
          <SMSStatsCard stats={stats} darkMode={darkMode} cardBg={cardBg} />
        </div>
        <HourlyHeatmap stats={stats} darkMode={darkMode} cardBg={cardBg} />
      </div>
    </div>
  )
}

interface HeaderProps {
  period: AnalyticsPeriod
  onChangePeriod: (p: AnalyticsPeriod) => void
  darkMode: boolean
}

const PERIOD_OPTIONS_BUILDER = (t: (k: string, fb: string) => string) => [
  { value: 'today' as AnalyticsPeriod, label: t('analytics.period.today', "Aujourd'hui") },
  { value: 'yesterday' as AnalyticsPeriod, label: t('analytics.period.yesterday', 'Hier') },
  { value: 'cette_semaine' as AnalyticsPeriod, label: t('analytics.period.thisWeek', 'Cette semaine') },
  { value: 'ce_mois' as AnalyticsPeriod, label: t('analytics.period.thisMonth', 'Ce mois') },
  { value: 'ce_trimestre' as AnalyticsPeriod, label: t('analytics.period.thisQuarter', 'Ce trimestre') },
  { value: 'cette_annee' as AnalyticsPeriod, label: t('analytics.period.thisYear', 'Cette année') },
]

/** Sélecteur de période seul (sans le H1 du Header). Utilisé quand AnalyticsTab
 *  est embed dans un panel qui fournit son propre header. */
function PeriodSelectOnly({
  period,
  onChange,
  darkMode,
}: {
  period: AnalyticsPeriod
  onChange: (p: AnalyticsPeriod) => void
  darkMode: boolean
}) {
  const { t } = useTranslation()
  const periodOptions = PERIOD_OPTIONS_BUILDER(t)
  return (
    <select
      value={period}
      onChange={(e) => onChange(e.target.value as AnalyticsPeriod)}
      className={cn(
        'rounded-lg h-9 px-3 border text-sm font-medium cursor-pointer transition-colors',
        darkMode
          ? 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700'
          : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50',
      )}
    >
      {periodOptions.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  )
}

function Header({ period, onChangePeriod, darkMode }: HeaderProps) {
  const { t } = useTranslation()
  const periodOptions = PERIOD_OPTIONS_BUILDER(t)

  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'p-2 rounded-xl',
            darkMode ? 'bg-indigo-500/10' : 'bg-indigo-50',
          )}
        >
          <BarChart3
            className={cn('w-6 h-6', darkMode ? 'text-indigo-400' : 'text-indigo-600')}
          />
        </div>
        <div>
          <h1
            className={cn(
              'text-2xl font-bold tracking-tight',
              darkMode ? 'text-white' : 'text-slate-900',
            )}
          >
            {t('analytics.title', 'Analytics')}
          </h1>
          <p
            className={cn(
              'text-sm',
              darkMode ? 'text-slate-400' : 'text-slate-500',
            )}
          >
            {t('analytics.subtitle', 'Vue d\'ensemble de votre activité')}
          </p>
        </div>
      </div>
      <select
        value={period}
        onChange={(e) => onChangePeriod(e.target.value as AnalyticsPeriod)}
        className={cn(
          'rounded-lg h-9 px-3 border text-sm font-medium cursor-pointer transition-colors',
          darkMode
            ? 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700'
            : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50',
        )}
      >
        {periodOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  )
}

interface KPIGridProps {
  stats: AgentStats
  darkMode: boolean
  cardBg: string
}

function KPIGrid({ stats, darkMode, cardBg }: KPIGridProps) {
  const { t } = useTranslation()
  const dur = formatDurationFromMinutes(stats.summary.total_real_minutes)
  const deltaPct = Math.round(stats.summary.delta_percentage)
  const positive = deltaPct >= 0

  const kpis = [
    {
      label: t('analytics.calls', 'Appels'),
      value: stats.summary.total_calls.toLocaleString(),
      icon: <Phone className="w-5 h-5" />,
    },
    {
      label: t('analytics.missed', 'Manqués'),
      value: stats.summary.missed_calls.toLocaleString(),
      icon: <PhoneMissed className="w-5 h-5" />,
    },
    {
      label: t('analytics.timeOnCalls', 'Temps en appel'),
      value: dur.value,
      unit: dur.unit,
      icon: <Timer className="w-5 h-5" />,
      delta: { pct: deltaPct, positive },
    },
    {
      label: t('analytics.smsSent', 'SMS envoyés'),
      value: stats.sms_stats.sent.toLocaleString(),
      icon: <MessageSquare className="w-5 h-5" />,
    },
  ]

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {kpis.map((kpi, i) => (
        <div
          key={i}
          className={cn('flex flex-col rounded-2xl p-5 border shadow-sm', cardBg)}
        >
          <div className="flex justify-between items-start mb-4">
            <p
              className={cn(
                'text-sm font-medium',
                darkMode ? 'text-slate-400' : 'text-slate-500',
              )}
            >
              {kpi.label}
            </p>
            <span className={darkMode ? 'text-slate-500' : 'text-slate-400'}>
              {kpi.icon}
            </span>
          </div>
          <div className="flex items-baseline gap-2">
            <p
              className={cn(
                'text-3xl font-bold tracking-tight',
                darkMode ? 'text-white' : 'text-slate-900',
              )}
            >
              {kpi.value}
            </p>
            {kpi.unit && (
              <span
                className={cn(
                  'text-xl font-semibold',
                  darkMode ? 'text-slate-400' : 'text-slate-500',
                )}
              >
                {kpi.unit}
              </span>
            )}
          </div>
          {kpi.delta && (
            <div className="flex items-center gap-2 mt-3">
              <span
                className={cn(
                  'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium',
                  kpi.delta.positive
                    ? darkMode
                      ? 'bg-emerald-500/10 text-emerald-400'
                      : 'bg-emerald-50 text-emerald-700'
                    : darkMode
                      ? 'bg-rose-500/10 text-rose-400'
                      : 'bg-rose-50 text-rose-700',
                )}
              >
                {kpi.delta.positive ? (
                  <TrendingUp className="w-3 h-3" />
                ) : (
                  <TrendingDown className="w-3 h-3" />
                )}
                {kpi.delta.positive ? '+' : ''}
                {kpi.delta.pct}%
              </span>
              <span
                className={cn(
                  'text-xs',
                  darkMode ? 'text-slate-500' : 'text-slate-400',
                )}
              >
                {t('analytics.vsPrevious', 'vs période précédente')}
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

interface ChartProps {
  stats: AgentStats
  darkMode: boolean
  cardBg: string
  locale: string
}

function CallVolumeChart({ stats, darkMode, cardBg, locale }: ChartProps) {
  const { t } = useTranslation()
  const chartData = useMemo(
    () =>
      stats.daily.map((day) => ({
        date: day.date,
        inbound: day.web_inbound.calls + day.phone_inbound.calls,
        outbound: day.web_outbound.calls + day.phone_outbound.calls,
      })),
    [stats.daily],
  )

  const CustomTooltip = ({ active, payload, label }: ChartTooltipProps) => {
    if (!active || !payload?.length || !label) return null
    const [y, m, d] = label.split('-').map(Number)
    const localDate = new Date(y, m - 1, d)
    return (
      <div
        className={cn(
          'px-3 py-2 rounded-lg shadow-lg border text-sm',
          darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200',
        )}
      >
        <p
          className={cn(
            'font-semibold mb-1',
            darkMode ? 'text-white' : 'text-slate-900',
          )}
        >
          {format(localDate, 'EEE dd MMM', {
            locale: locale === 'fr' ? fr : enCA,
          })}
        </p>
        {payload.map((entry, i) => (
          <p key={i} style={{ color: entry.color }} className="text-xs">
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    )
  }

  return (
    <div className={cn('rounded-2xl border shadow-sm overflow-hidden', cardBg)}>
      <div
        className={cn(
          'p-6 border-b',
          darkMode ? 'border-slate-700/50' : 'border-slate-200',
        )}
      >
        <h3
          className={cn(
            'text-base font-semibold',
            darkMode ? 'text-white' : 'text-slate-900',
          )}
        >
          {t('analytics.callVolume', "Volume d'appels")}
        </h3>
        <p
          className={cn(
            'text-sm mt-1',
            darkMode ? 'text-slate-400' : 'text-slate-500',
          )}
        >
          {t('analytics.callVolumeDesc', 'Évolution quotidienne')}
        </p>
      </div>
      <div className="p-6">
        <div className="flex items-center justify-end gap-4 mb-6">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-[#6467f2]" />
            <span
              className={cn(
                'text-xs',
                darkMode ? 'text-slate-400' : 'text-slate-500',
              )}
            >
              {t('analytics.inbound', 'Entrants')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-violet-500" />
            <span
              className={cn(
                'text-xs',
                darkMode ? 'text-slate-400' : 'text-slate-500',
              )}
            >
              {t('analytics.outbound', 'Sortants')}
            </span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData} barGap={2}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={darkMode ? '#334155' : '#e2e8f0'}
              vertical={false}
            />
            <XAxis
              dataKey="date"
              tickFormatter={(d: string) => {
                const [y, m, day] = d.split('-').map(Number)
                return format(new Date(y, m - 1, day), 'EEE', {
                  locale: locale === 'fr' ? fr : enCA,
                })
              }}
              stroke={darkMode ? '#64748b' : '#94a3b8'}
              tick={{ fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              stroke={darkMode ? '#64748b' : '#94a3b8'}
              tick={{ fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
            <Bar
              dataKey="inbound"
              fill="#6467f2"
              radius={[4, 4, 0, 0]}
              name={t('analytics.inbound', 'Entrants')}
            />
            <Bar
              dataKey="outbound"
              fill="#8b5cf6"
              radius={[4, 4, 0, 0]}
              name={t('analytics.outbound', 'Sortants')}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

interface SplitProps {
  stats: AgentStats
  darkMode: boolean
  cardBg: string
}

function DirectionSplit({ stats, darkMode, cardBg }: SplitProps) {
  const { t } = useTranslation()
  const inboundCalls = stats.by_direction.inbound.calls
  const outboundCalls = stats.by_direction.outbound.calls
  const total = inboundCalls + outboundCalls
  const inboundPct = total > 0 ? Math.round((inboundCalls / total) * 100) : 0
  const outboundPct = total > 0 ? 100 - inboundPct : 0

  return (
    <div className={cn('rounded-2xl border shadow-sm p-6', cardBg)}>
      <h3
        className={cn(
          'text-base font-semibold mb-1',
          darkMode ? 'text-white' : 'text-slate-900',
        )}
      >
        {t('analytics.direction', 'Direction')}
      </h3>
      <p
        className={cn(
          'text-sm mb-4',
          darkMode ? 'text-slate-400' : 'text-slate-500',
        )}
      >
        {t('analytics.directionDesc', 'Répartition des appels')}
      </p>

      <div className="space-y-4">
        <div>
          <div className="flex justify-between mb-1.5">
            <span
              className={cn(
                'text-xs font-medium',
                darkMode ? 'text-slate-300' : 'text-slate-700',
              )}
            >
              {t('analytics.inbound', 'Entrants')}
            </span>
            <span
              className={cn(
                'text-xs font-semibold tabular-nums',
                darkMode ? 'text-slate-200' : 'text-slate-800',
              )}
            >
              {inboundCalls} · {inboundPct}%
            </span>
          </div>
          <div
            className={cn(
              'h-2 rounded-full overflow-hidden',
              darkMode ? 'bg-slate-800' : 'bg-slate-100',
            )}
          >
            <div
              className="h-full bg-[#6467f2] rounded-full transition-all"
              style={{ width: `${inboundPct}%` }}
            />
          </div>
        </div>

        <div>
          <div className="flex justify-between mb-1.5">
            <span
              className={cn(
                'text-xs font-medium',
                darkMode ? 'text-slate-300' : 'text-slate-700',
              )}
            >
              {t('analytics.outbound', 'Sortants')}
            </span>
            <span
              className={cn(
                'text-xs font-semibold tabular-nums',
                darkMode ? 'text-slate-200' : 'text-slate-800',
              )}
            >
              {outboundCalls} · {outboundPct}%
            </span>
          </div>
          <div
            className={cn(
              'h-2 rounded-full overflow-hidden',
              darkMode ? 'bg-slate-800' : 'bg-slate-100',
            )}
          >
            <div
              className="h-full bg-violet-500 rounded-full transition-all"
              style={{ width: `${outboundPct}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

function SMSStatsCard({ stats, darkMode, cardBg }: SplitProps) {
  const { t } = useTranslation()
  return (
    <div className={cn('rounded-2xl border shadow-sm p-6', cardBg)}>
      <h3
        className={cn(
          'text-base font-semibold mb-1',
          darkMode ? 'text-white' : 'text-slate-900',
        )}
      >
        {t('analytics.sms', 'SMS')}
      </h3>
      <p
        className={cn(
          'text-sm mb-4',
          darkMode ? 'text-slate-400' : 'text-slate-500',
        )}
      >
        {t('analytics.smsDesc', 'Messages texte')}
      </p>
      <div className="grid grid-cols-2 gap-4">
        <StatItem
          label={t('analytics.sent', 'Envoyés')}
          value={stats.sms_stats.sent}
          darkMode={darkMode}
        />
        <StatItem
          label={t('analytics.received', 'Reçus')}
          value={stats.sms_stats.received}
          darkMode={darkMode}
        />
        <StatItem
          label={t('analytics.segments', 'Segments')}
          value={stats.sms_stats.segments}
          darkMode={darkMode}
        />
        <StatItem
          label={t('analytics.total', 'Total')}
          value={stats.sms_stats.total}
          darkMode={darkMode}
        />
      </div>
    </div>
  )
}

function StatItem({
  label,
  value,
  darkMode,
}: {
  label: string
  value: number
  darkMode: boolean
}) {
  return (
    <div>
      <p
        className={cn(
          'text-[10px] uppercase tracking-wider font-semibold',
          darkMode ? 'text-slate-500' : 'text-slate-400',
        )}
      >
        {label}
      </p>
      <p
        className={cn(
          'text-2xl font-bold tabular-nums',
          darkMode ? 'text-white' : 'text-slate-900',
        )}
      >
        {value.toLocaleString()}
      </p>
    </div>
  )
}

interface HeatmapProps {
  stats: AgentStats
  darkMode: boolean
  cardBg: string
}

function HourlyHeatmap({ stats, darkMode, cardBg }: HeatmapProps) {
  const { t } = useTranslation()
  const heatmapMax = useMemo(
    () => stats.hourly.reduce((max, h) => Math.max(max, h.count), 0) || 1,
    [stats.hourly],
  )
  const getCount = (day: number, hour: number): number =>
    stats.hourly.find((h) => h.day === day && h.hour === hour)?.count || 0

  const colorFor = (count: number): string => {
    if (count === 0) return darkMode ? '#334155' : '#f1f5f9'
    const intensity = count / heatmapMax
    if (intensity < 0.25)
      return darkMode ? 'rgba(100, 103, 242, 0.25)' : 'rgba(100, 103, 242, 0.2)'
    if (intensity < 0.5)
      return darkMode ? 'rgba(100, 103, 242, 0.45)' : 'rgba(100, 103, 242, 0.4)'
    if (intensity < 0.75)
      return darkMode ? 'rgba(100, 103, 242, 0.7)' : 'rgba(100, 103, 242, 0.65)'
    return '#6467f2'
  }

  return (
    <div className={cn('rounded-2xl border shadow-sm overflow-hidden', cardBg)}>
      <div
        className={cn(
          'p-6 border-b',
          darkMode ? 'border-slate-700/50' : 'border-slate-200',
        )}
      >
        <h3
          className={cn(
            'text-base font-semibold',
            darkMode ? 'text-white' : 'text-slate-900',
          )}
        >
          {t('analytics.heatmap', "Heatmap d'activité")}
        </h3>
        <p
          className={cn(
            'text-sm mt-1',
            darkMode ? 'text-slate-400' : 'text-slate-500',
          )}
        >
          {t('analytics.heatmapDesc', 'Nombre d\'appels par jour et heure')}
        </p>
      </div>
      <div className="p-6 overflow-x-auto">
        <div className="min-w-[640px]">
          {/* Header heures */}
          <div className="grid grid-cols-[40px_repeat(24,_minmax(0,_1fr))] gap-0.5 mb-1">
            <div />
            {Array.from({ length: 24 }, (_, i) => (
              <div
                key={i}
                className={cn(
                  'text-[9px] text-center',
                  darkMode ? 'text-slate-500' : 'text-slate-400',
                )}
              >
                {i}
              </div>
            ))}
          </div>
          {/* Grille jours × heures */}
          {DAYS_LABELS.map((dayLabel, dayIdx) => (
            <div
              key={dayLabel}
              className="grid grid-cols-[40px_repeat(24,_minmax(0,_1fr))] gap-0.5 mb-0.5"
            >
              <div
                className={cn(
                  'text-[10px] flex items-center font-medium',
                  darkMode ? 'text-slate-400' : 'text-slate-500',
                )}
              >
                {dayLabel}
              </div>
              {Array.from({ length: 24 }, (_, hour) => {
                const count = getCount(dayIdx, hour)
                return (
                  <div
                    key={hour}
                    className="aspect-square rounded-sm transition-transform hover:scale-110"
                    style={{ backgroundColor: colorFor(count) }}
                    title={`${dayLabel} ${hour}h - ${count} appel${count > 1 ? 's' : ''}`}
                  />
                )
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
